
import { Component, Vue, Prop } from 'vue-property-decorator';
import {
  calculateTimeSaved,
  calculateStockoutPrevention,
  calculateAnnualSavings,
  calculateLaborCostSavings,
} from '@/services/pricingCalculator';

/**
 * ROICalculator Component
 *
 * Interactive calculator showing time savings, labor cost savings, revenue protection,
 * and annual net savings from using Replenishment Center. Uses simplified formulas
 * based on real merchant data.
 *
 * @component
 * @example
 * ```vue
 * <!-- Default usage (rcMonthlyCost = $249) -->
 * <ROICalculator />
 *
 * <!-- Custom pricing -->
 * <ROICalculator :rc-monthly-cost="199" />
 * ```
 *
 * @remarks
 * Calculation Methodology:
 *
 * 1. **Time Saved** (calculateTimeSaved):
 *    - Simplified formula with base hours + incremental growth
 *    - Formula: ~10h base for <1000 SKUs, +1h per 10,000 additional SKUs
 *    - Range: 100-50000 SKUs → 10h to ~14.9h saved per month
 *
 * 2. **Labor Cost Savings** (calculateLaborCostSavings):
 *    - Converts time saved to dollar value
 *    - Formula: timeSaved × $20/hour
 *    - Example: 10 hours saved = $200 labor cost reduction
 *
 * 3. **Stockout Prevention Savings** (calculateStockoutPrevention):
 *    - Average stockout loss rate: 1% of total revenue
 *    - Formula: (monthlyOrders × orderValue) × 0.01
 *    - Example: 2500 orders × $100 = $250k revenue → $2,500 protected
 *
 * 4. **Annual Net Savings** (calculateAnnualSavings):
 *    - Formula: (monthlyProtected × 12) + (monthlyLabor × 12) - (rcMonthlyCost × 12)
 *    - Example: ($2,500 × 12) + ($200 × 12) - ($249 × 12) = $29,412 annual savings
 *    - Includes both labor savings and stockout prevention benefits
 *
 * Default Values (showing realistic ~10h, ~$2500/month):
 * - SKUs: 400
 * - Order Value: $100
 * - Monthly Orders: 2500
 *
 * Interactive Ranges:
 * - SKUs: 100-50000 (step: 500)
 * - Order Value: $25-$500 (step: $25)
 * - Monthly Orders: 500-10000 (step: 250)
 *
 * @see pricingCalculator.ts - Calculation formulas and documentation
 */
@Component({
  components: {},
})
export default class ROICalculator extends Vue {
  /**
   * Monthly cost of Replenishment Center subscription
   * Must be a positive number greater than 0
   */
  @Prop({
    default: 249,
    type: Number,
    validator: (value: number) => {
      if (value <= 0) {
        console.warn(`[ROICalculator] Invalid rcMonthlyCost: ${value}. Must be greater than 0. Falling back to default (249).`);
        return false;
      }
      return true;
    }
  })
  public rcMonthlyCost!: number;

  /**
   * Number of SKUs in merchant's catalog
   * @default 400 - Shows realistic ~10 hours saved
   */
  private skuCount: number = 400;

  /**
   * Average order value in dollars
   * @default 100 - Mid-range e-commerce AOV
   */
  private averageOrderValue: number = 100;

  /**
   * Monthly order volume
   * @default 2500 - Shows realistic ~$3000 revenue protected
   */
  private monthlyOrders: number = 2500;

  /**
   * Calculate time saved per month based on SKU count
   *
   * Uses simplified formula with base hours + incremental growth.
   * More realistic for small to medium merchants.
   *
   * @returns {number} Hours saved per month (1 decimal precision)
   *
   * @remarks
   * Formula:
   * - Base: ~10 hours for stores with <1000 SKUs
   * - Growth: +1 hour per additional 10,000 SKUs above 1000
   *
   * Examples:
   * - 500 SKUs = 10 hours
   * - 11000 SKUs = 11 hours
   * - 50000 SKUs = 14.9 hours
   *
   * @see calculateTimeSaved - Implementation in pricingCalculator.ts
   */
  get timeSaved(): number {
    return calculateTimeSaved(this.skuCount);
  }

  /**
   * Format timeSaved for display
   *
   * Returns time saved as an integer string when it's a whole number,
   * otherwise returns it with one decimal place.
   *
   * @returns {string} Formatted time saved value
   */
  get formattedTimeSaved(): string {
    return this.timeSaved % 1 === 0 ? this.timeSaved.toString() : this.timeSaved.toFixed(1);
  }

  /**
   * Validated monthly cost with fallback to default
   *
   * Ensures rcMonthlyCost is always a positive number. If the prop value
   * is invalid (zero or negative), falls back to the default of 249.
   *
   * @returns {number} Valid monthly cost (always > 0)
   */
  get validatedRcMonthlyCost(): number {
    return this.rcMonthlyCost > 0 ? this.rcMonthlyCost : 249;
  }

  /**
   * Calculate monthly labor cost savings from time automation
   *
   * Converts time saved into dollar value based on $20/hour labor rate.
   * Shows merchants the direct labor cost reduction from automation.
   *
   * @returns {number} Monthly labor cost savings (2 decimal precision)
   *
   * @remarks
   * Formula: timeSaved × $20/hour
   *
   * Examples:
   * - 10 hours saved = $200 saved
   * - 15 hours saved = $300 saved
   * - 50 hours saved = $1,000 saved
   *
   * @see calculateLaborCostSavings - Implementation in pricingCalculator.ts
   */
  get laborCostSavings(): number {
    return calculateLaborCostSavings(this.timeSaved);
  }

  /**
   * Calculate monthly revenue protected from stockout prevention
   *
   * Based on 1% average stockout loss rate. Shows merchants
   * how much revenue they're protecting by avoiding out-of-stock situations.
   *
   * @returns {number} Monthly revenue protected (2 decimal precision)
   *
   * @remarks
   * Formula: (monthlyOrders × orderValue) × 0.01
   *
   * Industry context:
   * - Average stockout rate: 1% of total revenue
   * - Stockouts cause immediate lost sales
   * - Also damage customer loyalty (repeat purchase rate)
   *
   * Example calculations:
   * - 1000 orders/month @ $50 = $500 protected
   * - 2500 orders/month @ $100 = $2500 protected
   * - 5000 orders/month @ $75 = $3750 protected
   *
   * @see calculateStockoutPrevention - Implementation in pricingCalculator.ts
   */
  get stockoutPrevention(): number {
    return calculateStockoutPrevention(this.monthlyOrders, this.averageOrderValue);
  }

  /**
   * Calculate annual net savings after paying for Replenishment Center
   *
   * Shows total value delivered to merchant including both labor savings and
   * revenue protection, minus the subscription cost. This is the "bottom line" ROI metric.
   *
   * @returns {number} Annual net savings in dollars (2 decimal precision)
   *
   * @remarks
   * Formula: (monthlyProtected × 12) + (monthlyLabor × 12) - (rcMonthlyCost × 12)
   *
   * Example for default values:
   * - Monthly stockout protection: $2,500
   * - Monthly labor savings: $200
   * - RC monthly cost: $249
   * - Annual stockout benefit: $2,500 × 12 = $30,000
   * - Annual labor benefit: $200 × 12 = $2,400
   * - Annual cost: $249 × 12 = $2,988
   * - Net savings: $30,000 + $2,400 - $2,988 = $29,412
   *
   * This represents total combined benefit from automation and stockout prevention.
   *
   * @see calculateAnnualSavings - Implementation in pricingCalculator.ts
   */
  get annualSavings(): number {
    return calculateAnnualSavings(this.stockoutPrevention, this.laborCostSavings, this.validatedRcMonthlyCost);
  }

  /**
   * Emit calculator interaction event with current values
   *
   * Fires when user adjusts any slider. Throttled to avoid excessive events.
   * Parent component handles analytics tracking.
   *
   * @fires calculator-interaction - Emits current ROI calculation data
   */
  public emitCalculatorInteraction(): void {
    this.$emit('calculator-interaction', {
      skuCount: this.skuCount,
      averageOrderValue: this.averageOrderValue,
      monthlyOrders: this.monthlyOrders,
      timeSaved: this.timeSaved,
      laborCostSavings: this.laborCostSavings,
      stockoutPrevention: this.stockoutPrevention,
      annualSavings: this.annualSavings,
    });
  }
}
